/* global fbq */

import React, { useState, useEffect } from 'react';
import LPTooltip from '../../components/LPTooltip';
import PremiumSelect from '../../components/PremiumSelect';
import { faUserGroup, faUserClock, faWifi, faUser, faLocationCrosshairs, faUserShield, faUsers, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

import { Helmet } from 'react-helmet-async';

import OG from '../../assets/og/division.png';

const RANKS = [
  'Iron IV','Iron III','Iron II','Iron I',
  'Bronze IV','Bronze III','Bronze II','Bronze I',
  'Silver IV','Silver III','Silver II','Silver I',
  'Gold IV','Gold III','Gold II','Gold I',
  'Platinum IV','Platinum III','Platinum II','Platinum I',
  'Emerald IV','Emerald III','Emerald II','Emerald I',
  'Diamond IV','Diamond III','Diamond II'
];

const DESIRED_RANKS = [
  ...RANKS,
  'Diamond I'
];

function romanToIntItoIV(s) {
  switch (s) {
    case 'I':   return 1;
    case 'II':  return 2;
    case 'III': return 3;
    case 'IV':  return 4;
    default:
      throw new Error(`Unsupported numeral: ${s}`);
  }
}

const roles = [
  { key: 'Top', label: 'Top', icon: require('../../assets/Positions/Top.png') },
  { key: 'Jungle', label: 'Jungle', icon: require('../../assets/Positions/Jungle.png') },
  { key: 'Mid', label: 'Mid', icon: require('../../assets/Positions/Mid.png') },
  { key: 'ADC', label: 'ADC', icon: require('../../assets/Positions/ADC.png') },
  { key: 'Support', label: 'Support', icon: require('../../assets/Positions/Support.png') },
];

const rawChamps = require('../../constants/champions.json');
const champions = Array.isArray(rawChamps) ? rawChamps : Object.values(rawChamps);

export default function DivisionBoosting({ updatePrice, order_details, checkoutInit, loading }) {
  const { getAccessToken } = useAuth();
  const currency = localStorage.getItem('currency') || 'USD';

  const [currentRank, setCurrentRank] = useState(RANKS[8]);
  const [desiredRank, setDesiredRank] = useState(DESIRED_RANKS[12]);
  const [queueType, setQueueType] = useState('Solo/Duo');
  const [region, setRegion] = useState('NA');
  const [lpGain, setLpGain] = useState('23-29');
  const [currentLp, setCurrentLp] = useState('0-33');
  const [addons, setAddons] = useState([]);
  const [searchChamp, setSearchChamp] = useState('');
  const [selectedChamps, setSelectedChamps] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [addonsOpen, setAddonsOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [initialLoad, setInitialLoad] = useState(true);

  const pushToDataLayer = (payload) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
  };

  // Initial load timer
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoad(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // Load stored data or calculate on mount
  useEffect(() => {
    const stored = localStorage.getItem('division-boosting-data');
    if (stored) {
      const parsed = JSON.parse(stored);
      setCurrentRank(parsed.currentRank || RANKS[8]);
      setDesiredRank(parsed.desiredRank || DESIRED_RANKS[12]);
      setQueueType(parsed.queueType || 'Solo/Duo');
      setRegion(parsed.region || 'NA');
      setLpGain(parsed.lpGain || '23-29');
      setCurrentLp(parsed.currentLp || '0-33');
      setAddons(parsed.addons || []);
      setSelectedChamps(parsed.selectedChamps || []);
      setSelectedRoles(parsed.selectedRoles || []);
      // then calculate price
      calculatePrice();
      order_details([
        ['Boost Type', 'Division Boosting'],
        ['Progress', `${parsed.currentRank || RANKS[8]} → ${parsed.desiredRank || DESIRED_RANKS[12]}`],
        ['Queue', parsed.queueType || 'Solo/Duo'],
        ['Region', parsed.region || 'NA']
      ]);
    } else {
      calculatePrice();
      order_details([
        ['Boost Type', 'Division Boosting'],
        ['Progress', `${RANKS[8]} → ${DESIRED_RANKS[12]}`],
        ['Queue', 'Solo/Duo'],
        ['Region', 'NA']
      ]);
    }
  }, []);

  // Recalculate on key state changes
  useEffect(() => {
    if(initialLoad) return;
    updateOrderDataCookie();
    calculatePrice();
  }, [currentRank, desiredRank, lpGain, currentLp, addons]);

  useEffect(() => {
    if (initialLoad) return;
    updateOrderDataCookie();
  }, [selectedChamps, selectedRoles, region, queueType]);

  const updateOrderDataCookie = () => {
    order_details([
      ['Boost Type', 'Division Boosting'],
      ['Progress', `${currentRank} → ${desiredRank}`],
      ['Queue', queueType],
      ['Region', region]
    ]);
    const data = { currentRank, desiredRank, queueType, region, lpGain, currentLp, addons, selectedChamps, selectedRoles };
    localStorage.setItem('division-boosting-data', JSON.stringify(data));
  }

  const calculatePrice = () => {
    loading(true);
    if(initialLoad) {
      //use the stored data to calculate the price
      const stored = localStorage.getItem('division-boosting-data');
      if (stored) {
        const parsed = JSON.parse(stored);
        const rank = parsed.currentRank.split(' ')[0];
        const division = romanToIntItoIV(parsed.currentRank.split(' ')[1]);
        const desiredRankParsed = parsed.desiredRank.split(' ')[0];
        const desiredDivision = romanToIntItoIV(parsed.desiredRank.split(' ')[1]);
        //lpGain is a range so we need to get the first value of the range
        const lpGainParsed = parsed.lpGain.split('-')[0]; // get the first value of the range... special case for 30+ extract 30
        // check if the lp gain is 30+ and set it to 30
        const lpGainRaw = lpGainParsed === '30+' ? 30 : parseInt(lpGainParsed);
        // currentLp is a range so we need to get the first value of the range
        const currentLpParsed = parseInt(parsed.currentLp.split('-')[0]);
        axios.post(process.env.REACT_APP_API_URL + '/price-calculation-division-boost', {
          rank,
          division,
          LP: currentLpParsed,
          desiredRank: desiredRankParsed,
          desiredDivision,
          LPGains: lpGainRaw,
          addons: parsed.addons,
          currency
        }).then((response) => {
          loading(false);
          updatePrice(response.data.totalPrice);
        }).catch((error) => {
          setSnackbar({ open: true, severity: 'error', message: 'Failed to calculate price. Please try again.' });
          console.log(error);
        });
        return;
      }
    } 
    const rank = currentRank.split(' ')[0];
    const division = romanToIntItoIV(currentRank.split(' ')[1]); 
    const desiredRankParsed = desiredRank.split(' ')[0];
    const desiredDivision = romanToIntItoIV(desiredRank.split(' ')[1]);
    //lpGain is a range so we need to get the first value of the range
    const lpGainParsed = lpGain.split('-')[0]; // get the first value of the range... special case for 30+ extract 30
    // check if the lp gain is 30+ and set it to 30
    const lpGainRaw = lpGainParsed === '30+' ? 30 : parseInt(lpGainParsed);
    // currentLp is a range so we need to get the first value of the range
    const currentLpParsed = parseInt(currentLp.split('-')[0]);
    // check if the currentLp is 0-33 and set it to 0
    axios.post(process.env.REACT_APP_API_URL + '/price-calculation-division-boost', { 
      rank,
      division,
      LP: currentLpParsed,
      desiredRank: desiredRankParsed,
      desiredDivision: desiredDivision,
      LPGains: lpGainRaw,
      addons,
      currency
    }).then((response) => {
        loading(false);
        updatePrice(response.data.totalPrice);
        order_details([
          ['Boost Type', 'Division Boosting'],
          ['Progress', `${currentRank} → ${desiredRank}`],
          ['Queue', queueType],
          ['Region', region]
        ]);
    }).catch((error) => {
        setSnackbar({ open: true, severity: 'error', message: 'Failed to calculate price. Please try again.' });
        console.log(error);
    });
  };

  //checkoutInit is passed, update the checkout state
  useEffect(() => {
    if (checkoutInit) {
      let mergedAddons = [...addons, ...checkoutInit.addons];
      //parse the data to send to the server
      let rank = currentRank.split(' ')[0];
      let division = romanToIntItoIV(currentRank.split(' ')[1]);
      let desiredRankParsed = desiredRank.split(' ')[0];
      let desiredDivision = romanToIntItoIV(desiredRank.split(' ')[1]);
      let lpGainParsed = lpGain.split('-')[0]; // get the first value of the range... special case for 30+ extract 30
      let lpGainRaw = lpGainParsed === '30+' ? 30 : parseInt(lpGainParsed);
      let currentLpParsed = parseInt(currentLp.split('-')[0]);
      let division_boosting = {
        rank: rank,
        division: division,
        LP: currentLpParsed,
        queue_type: queueType,
        desired_rank: desiredRankParsed,
        desired_division: desiredDivision,
        LP_gains: lpGainRaw,
        addons: mergedAddons,
        roles: selectedRoles,
        champions: selectedChamps
      }
      if (typeof fbq !== 'undefined') {
        fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: ['division-boosting'],
          content_name: 'Division Boosting',
          currency: currency,
          value: checkoutInit.price,
          num_items: 1,
          contents: [
            {
              id: 'division-boosting',
              quantity: 1,
              item_price: checkoutInit.price
            }
          ]
        });
      }
      pushToDataLayer({
        event: 'begin_checkout',
        ecommerce: {
          value:    checkoutInit.price,
          currency: currency
        }
      })
      axios.post(process.env.REACT_APP_API_URL + `/user/stripe-checkout`, {
        order_type: 'boost', 
        boost_type: 'division_boosting', 
        server: region, 
        coupon: checkoutInit.coupon, 
        division_boosting: division_boosting,
        currency: currency
      }, { headers: { Authorization: getAccessToken() }
      }).then((response) => {
          window.location.href = response.data.url;
      }).catch((error) => {
          console.log(error);
      });
    }
  }, [checkoutInit]);

  const handleClose = () => {
    setSnackbar(s => ({ ...s, open: false }));
  };

  const toggleAddon = label => {
    setAddons(prev => {
      let next = prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label];
      if (label === 'Duo') next = next.filter(l => l !== 'Solo Only');
      if (label === 'Solo Only') next = next.filter(l => l !== 'Duo');
      return next;
    });
  };

  const toggleRole = key => {
    setSelectedRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  };

  const toggleChamp = name => {
    setSelectedChamps(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
  };

  const filtered = champions.filter(c => c.toLowerCase().includes(searchChamp.toLowerCase()));
  const desiredOptions = DESIRED_RANKS.filter((_, i) => i > DESIRED_RANKS.indexOf(currentRank));

  useEffect(() => {
  // find both in DESIRED_RANKS
  const currentIdx = DESIRED_RANKS.indexOf(currentRank);
  const desiredIdx = DESIRED_RANKS.indexOf(desiredRank);

  // if desired is at or below current, bump it up one slot
  if (desiredIdx <= currentIdx) {
    setDesiredRank(DESIRED_RANKS[currentIdx + 1]);
  }
}, [currentRank, desiredRank]);


  useEffect(() => {
    if(initialLoad) return;
    if (!addons.includes('Specific Roles')) setSelectedRoles([]);
    if (!addons.includes('Specific Champions')) setSelectedChamps([]);
  }, [addons]);

  const emblemName = rank => rank.split(' ')[0];


  return (
    <>  
      <Helmet>
        <meta charSet="utf-8" />
        <title>LoL Division Boosting – Fast &amp; Safe Rank Ups | EloFactory.gg</
        title>
        <meta name="description" content="Climb divisions with our LoL elo boost service. Professional boosters, secure VPN usage, and affordable rates. Reach your desired rank safely – start your boost today!" />
        <link rel="canonical" href="https://elofactory.gg/get-boosting/division-boosting" />
        <meta property="og:type" content="website" />
        <meta property="og:title"
        content="LoL Division Boosting – Achieve Higher Rank Fast" />
        <meta property="og:description" content="Professional Elo boosting to raise your League division. Safe, quick, and anonymous service by high-elo players. Get to the next rank now!" />
        <meta property="og:url" content="https://elofactory.gg/get-boosting/division-boosting" />
        <meta property="og:image" content={OG} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="EloFactory.gg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LoL Division Boost – Fast Climb to Your Desired Rank" />
        <meta name="twitter:description" content="Boost your League rank with our division boosting service. Cheap prices, Challenger-level boosters, and secure VPN protection. Climb safely!" />
        <meta name="twitter:image" content={OG} />
        <meta name="twitter:site" content="@elofactorygg" />
        <meta name="twitter:creator" content="@elofactorygg" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hrefLang="en-US" href="https://elofactory.gg/get-boosting/division-boosting" />
        <script type="application/ld+json">{`
        {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "League of Legends Division Boosting",
        "description": "Division elo boosting service to help League of Legends players reach higher divisions quickly.",
        "provider": {
        "@type": "Organization",
        "name": "EloFactory",
        "url": "https://elofactory.gg"
        },
        "areaServed": "North America",
        "serviceType": "Rank Division Boosting",
        "url": "https://elofactory.gg/get-boosting/division-boosting"
        }
        `}</script>
      </Helmet>

      {/* Rank Section */}
      <div
        className="
          rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white
          flex flex-col md:flex-row gap-8
        "
      >
        {/* Current Rank */}
        <div className="flex-1 md:border-r md:border-[var(--border)] md:pr-6">
          <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Current Rank</h2>
          <div className="flex items-start gap-4 items-center">
            <img
              id="currentEmblem"
              src={require(`../../assets/Emblems/${emblemName(currentRank)}_Emblem.png`)}
              alt={`${currentRank} Rank Icon`}
              title={`League of Legends ${currentRank} rank`}
              className="h-28 w-28 object-contain"
              width={112}    // 28 * 4px = 112px
              height={112}
            />
            <div className="flex w-full flex-col gap-3">
              <PremiumSelect
                value={currentRank}
                onChange={setCurrentRank}
                options={RANKS}
                label="Rank & Division"
              />
              <PremiumSelect
                label="Current LP"
                value={currentLp}
                onChange={setCurrentLp}
                options={['0-33', '34-66', '67-100']}
              />
            </div>
          </div>
        </div>

        {/* Desired Rank */}
        <div className="flex-1">
          <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Desired Rank</h2>
          <div className="flex items-start gap-4 items-center">
            <img
              id="desiredEmblem"
              src={require(`../../assets/Emblems/${emblemName(desiredRank)}_Emblem.png`)}
              alt={`${desiredRank} Rank Icon`}
              title={`League of Legends ${desiredRank} rank`}
              className="h-28 w-28 object-contain"
              width={112}    // 28 * 4px = 112px
              height={112}
            />
            <div className="flex w-full flex-col gap-3">
              <PremiumSelect
                value={desiredRank}
                onChange={setDesiredRank}
                options={desiredOptions}
                label="Rank & Division"
              />
              <PremiumSelect
                label="Current LP Gains"
                value={lpGain}
                onChange={setLpGain}
                options={['10-22', '23-29', '30+']}
                labelTooltipText="Estimate how much LP you gain per win (e.g. 18–22 LP)."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Queue & Region */}
      <div className="rounded-lg bg-[var(--bg-surface)] border border-[var(--border)] p-6 text-white">
          <h2 className="mb-4 text-lg font-semibold text-[#00baff]">Queue & Region</h2>
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[200px] flex-1">
              <PremiumSelect
                label="Queue Type"
                value={queueType}
                onChange={setQueueType}
                options={['Solo/Duo', 'Flex']}
              />
            </div>
            <div className="min-w-[200px] flex-1">
                <PremiumSelect
                label="Account Region"
                value={region}
                onChange={setRegion}
                options={['NA', 'EUW', 'EUNE', 'LAN']}
                />
            </div>
          </div>
      </div>

      {/* Add-ons */}
      <div className={`
        rounded-lg bg-[var(--bg-surface)] border 
        border-[var(--border)] p-6 text-white flex 
        flex-col md:flex-row md:cursor-auto ${addonsOpen ? 'cursor-auto' : 'cursor-pointer'}`}
        onClick={() => {
          //check if it is mobile
          if (window.innerWidth <= 768 && !addonsOpen) {
            setAddonsOpen(true)
            return
          }
        }}
      >
        {/* ─── Left: tight add-ons list ─── */}
        {/* Header + toggle: only on mobile */}
        <div className={`flex items-center justify-between md:hidden cursor-pointer md:cursor-auto`}
        onClick={() => {
          if (window.innerWidth <= 768 && addonsOpen) {
            setAddonsOpen(!addonsOpen)
            return
          }
        }}>
          <h2 className="text-lg font-semibold text-[#00BAFF]">Advanced Settings</h2>
          <FontAwesomeIcon
              icon={faChevronDown}
              className={`
                text-white
                transform transition-transform duration-300 ease-in-out
                ${addonsOpen ? 'rotate-180' : 'rotate-0'}
              `}
              width={16}
              height={16}
            />        
          </div>
        <div
        className={`
          overflow-hidden transition-[max-height] duration-300 ease-in-out
          ${addonsOpen ? 'max-h-[2000px]' : 'max-h-0'}
          md:max-h-none md:block space-y-4
        `}
      >
        <h2 className="text-lg font-semibold text-[#00baff] hidden md:block">Advanced Settings</h2>
          {[
            ['Duo', '+60%', 'Play alongside the booster in Duo Queue.', faUserGroup],
            ['Priority', '+15%', 'Your order will be prioritized before others.', faUserClock],
            ['Solo Only', '+25%', 'The booster will not duo queue with anyone.', faUser],
            ['Streaming', '+15%', 'Watch your boost live via private stream.', faWifi],
            ['Bonus Win', 'RANK BASED', 'One extra win guaranteed; price varies.', faUserShield]
          ].map(([label, extra, desc, icon]) => (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center">
                <FontAwesomeIcon icon={icon} className="w-4 h-4 mr-2 text-[#00baff]" />
                <span className="text-sm text-white mr-2">{label}</span>
                <span className="text-sm text-[#00baff] mr-2">{extra}</span>
                <LPTooltip tooltipText={desc} />
              </div>
              <input
                type="checkbox"
                className="toggle-switch ml-2"
                checked={addons.includes(label)}
                onChange={() => toggleAddon(label)}
              />
            </div>
          ))}

          <div className="space-y-4">
            {/* Header with toggle */}
            <div className="flex items-center justify-between" key="Specific Roles">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faLocationCrosshairs}
                  className="w-4 h-4 mr-2 text-[#00baff]"
                />
                <span className="text-sm text-white mr-2">Specific Roles</span>
                <span className="text-sm text-[#00baff] mr-2">+15%</span>
                <LPTooltip tooltipText="Choose a preferred role (e.g. Mid, ADC)." />
              </div>
              <input
                type="checkbox"
                className="toggle-switch"
                checked={addons.includes("Specific Roles")}
                onChange={() => toggleAddon("Specific Roles")}
              />
            </div>

            {/* Role buttons */}
            <div className={`
              flex flex-wrap gap-2 justify-between
              ${addons.includes("Specific Roles") ? "flex" : "hidden"} md:flex
            `}>
              {roles.map(({ key, icon }) => (
                <button
                  key={key}
                  onClick={() => toggleRole(key)}
                  disabled={!addons.includes("Specific Roles")}
                  aria-pressed={selectedRoles.includes(key)}
                  className={
                    `
                    group relative flex items-center justify-center
                    rounded-md
                    transition-transform transition-shadow transition-opacity duration-200 ease-out
                    motion-reduce:transition-none
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      selectedRoles.includes(key)
                        ? "shadow-[0_0_6px_rgba(0,186,255,0.6)]  bg-white/10"
                        : "shadow-sm bg-transparent"
                    }
                    hover:shadow-[0_0_4px_rgba(0,186,255,0.4)] hover:bg-white/5
                    `
                  }
                >
                  <img
                    src={icon}
                    alt={`${key} Role Icon`}
                    title={`League of Legends ${key} role`}
                    className="w-8 h-8"
                    width={32}    // 8 * 4px = 32px
                    height={32}
                  />
                </button>
              ))}
            </div>
          </div>
          
        </div>

        {/* ─── Divider ─── */}
        <div className="mx-6 border-l border-[var(--border)] hidden md:block" />

        {/* ─── Right: full-width panels ─── */}
        <div className={`flex-1 space-y-4 pl-0 md:pl-2
          overflow-hidden transition-all duration-300 ease-in-out
          ${addonsOpen ? 'max-h-[2000px] mt-4' : 'max-h-0 mt-0 space-y-0'}
          md:max-h-none md:block`}
        >
          {/* Header */}
          {/* Specific Champions panel */}
          <div className="space-y-4">
            {/* header + toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUsers} className="w-4 h-4 mr-2 text-[#00baff]" />
                <span className="text-sm text-white mr-2">Specific Champions</span>
                <span className="text-sm text-[#00baff] mr-2">+20%</span>
                <LPTooltip tooltipText="Choose specific champions to play." />
              </div>
              <input
                type="checkbox"
                className="toggle-switch"
                checked={addons.includes("Specific Champions")}
                onChange={() => toggleAddon("Specific Champions")}
              />
            </div>

            {/* ─── Mobile-hide / desktop-show wrapper ─── */}
          <div className={`${addons.includes("Specific Champions") ? 'block' : 'hidden'} md:block space-y-2`}>
            
            {/* ─── Search box (unstyled, just your original class) ─── */}
            <input
              type="text"
              placeholder="Search champions…"
              value={searchChamp}
              onChange={e => {
                setSearchChamp(e.target.value)
              }}
              disabled={!addons.includes("Specific Champions")}
              className="search-input w-full rounded-md bg-[var(--bg-surface)] border border-[var(--border)] px-3 py-2 text-sm text-white placeholder-[var(--secondary-text)] focus:outline-none focus:ring-1 focus:ring-[var(--focus-ring)]"
            />

            {/* ─── Champions container: grid when enabled, .is-disabled when off ─── */}
            <div
              className={`
                champions-container
                scrollbar-custom
                h-52
                ${addons.includes("Specific Champions")
                  ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 overflow-y-auto max-h-64 rounded-md bg-[var(--bg-surface)] border border-[var(--border)]'
                  : 'is-disabled'}
              `}
            >
              {filtered.map(champ => (
                <label key={champ} className="champion-item flex items-center p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedChamps.includes(champ)}
                    onChange={() => {
                      //champion selection maximum of 10
                      if (selectedChamps.length >= 10 && !selectedChamps.includes(champ)) {
                        setSnackbar({ open: true, severity: 'warning', message: 'You can only select up to 10 champions.' });
                        return;
                      }
                      toggleChamp(champ)
                    }}
                    disabled={!addons.includes("Specific Champions")}
                  />
                  <span className="champion-name">{champ}</span>
                </label>
              ))}
            </div>
          </div>

          </div>
        </div>
      </div>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}